import { AlertBanner, Button, Skeleton } from '@curri/components'
import { useState, type ReactNode } from 'react'

import {
  WEBHOOK_READ_SCOPE,
  WEBHOOK_WRITE_SCOPE,
  type WebhookEndpointConfig,
} from '../../api/webhooks.ts'
import { useAuth } from '../../auth/AuthProvider.tsx'
import { useGhost } from '../../ghost/GhostProvider.tsx'
import { EndpointEditor } from '../../routes/webhooks/EndpointsCard.tsx'
import {
  describeFailure,
  isLegacySource,
  LEGACY_SOURCE_NOTE,
  VARIANT_LABELS,
} from '../../routes/webhooks/format.ts'
import { Pill } from '../../routes/webhooks/Pill.tsx'
import {
  isSignedVariant,
  isVariant,
  VARIANTS,
  variantTone,
} from '../../routes/webhooks/types.ts'
import { useWebhookConfig } from '../../routes/webhooks/useWebhookData.ts'

// The inline endpoint configurator in the webhook docs' getting-started flow,
// mounted from markdown via `::webhook-endpoint{variant="…"}`
// (src/docs/directives.tsx). Same form and same rules as the dashboard's
// Endpoints card — it *is* the dashboard's editor (EndpointsCard.tsx), wrapped
// in the docs' states: sign in first, then configure without leaving the page.
//
// Hiding the form without webhook:write or while ghosting is UX, not the
// security boundary — the server enforces both on the PUT.

type Variant = WebhookEndpointConfig['variant']

const Card = ({ children }: { children: ReactNode }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      margin: '8px 0 16px',
      padding: '16px 18px',
      border: '1px solid var(--curri-border)',
      borderRadius: 'var(--curri-radius-md)',
      background: 'var(--curri-surface)',
    }}
  >
    {children}
  </div>
)

const Note = ({ children }: { children: ReactNode }) => (
  <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: 'var(--curri-text-secondary)' }}>
    {children}
  </p>
)

const SignInCard = ({ variant }: { variant: Variant }) => {
  const { state, signIn } = useAuth()
  const authenticating = state.status === 'authenticating'

  return (
    <Card>
      <Pill tone={variantTone(variant)}>{VARIANT_LABELS[variant]}</Pill>
      <Note>
        Sign in to configure your {VARIANT_LABELS[variant].toLowerCase()}{' '}
        endpoint right here — the form edits your own account&apos;s webhook
        settings, so it needs to know who you are.
      </Note>
      <div>
        <Button
          disabled={authenticating}
          label={authenticating ? 'Signing in…' : 'Sign in'}
          onClick={signIn}
          size="small"
          type="button"
          variant="secondary"
        />
      </div>
    </Card>
  )
}

const ConfiguredRow = ({
  endpoint,
  onEdit,
}: {
  endpoint: WebhookEndpointConfig
  onEdit?: () => void
}) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
    <code
      style={{
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 13,
        color: 'var(--curri-text)',
        overflowWrap: 'anywhere',
      }}
    >
      {endpoint.url}
    </code>
    <span style={{ flex: '1 1 24px' }} />
    <span
      style={{
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 11.5,
        color: endpoint.authHeaderName
          ? 'var(--curri-text-secondary)'
          : 'var(--curri-text-tertiary)',
      }}
    >
      {endpoint.authHeaderName
        ? `Auth header: ${endpoint.authHeaderName}`
        : 'No auth header'}
      {isLegacySource(endpoint.source) && ` · ${LEGACY_SOURCE_NOTE}`}
    </span>
    {onEdit && (
      <Button label="Edit" onClick={onEdit} size="small" type="button" variant="tertiary" />
    )}
  </div>
)

const Configurator = ({ variant }: { variant: Variant }) => {
  const { resource, refresh } = useWebhookConfig()
  const { state } = useAuth()
  const { target: ghostTarget } = useGhost()
  const [editing, setEditing] = useState(false)
  const [dismissedEmptyEditor, setDismissedEmptyEditor] = useState(false)
  const [justSaved, setJustSaved] = useState(false)

  const grantedScopes =
    state.status === 'authenticated' ? state.caller.grantedScopes : []
  const canEdit =
    grantedScopes.includes(WEBHOOK_WRITE_SCOPE) && ghostTarget === null

  if (resource.status === 'loading') {
    return (
      <Card>
        <Skeleton style={{ height: 20 }} />
        <Skeleton style={{ height: 20, width: '60%' }} />
      </Card>
    )
  }

  if (resource.status === 'failed') {
    const copy = describeFailure(resource.failure)
    return (
      <Card>
        <AlertBanner title={copy.title} type={copy.tone}>
          {copy.body}
        </AlertBanner>
      </Card>
    )
  }

  const endpoint = resource.data.find(config => config.variant === variant)
  // An unconfigured endpoint opens the form directly — this is a setup flow,
  // and "Not configured [Add]" is a click that says nothing new.
  const showEditor = canEdit && (editing || (!endpoint && !dismissedEmptyEditor))

  return (
    <Card>
      {/* The editor renders its own variant pill, so this header would be a
          second copy of it while editing. */}
      {!showEditor && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Pill tone={variantTone(variant)}>{VARIANT_LABELS[variant]}</Pill>
          {justSaved && (
            <span style={{ fontSize: 12.5, color: 'var(--curri-accent)' }}>
              Saved — takes effect on the next webhook Curri sends.
            </span>
          )}
        </div>
      )}

      {showEditor && isSignedVariant(variant) && (
        <Note>
          Saving issues this endpoint&apos;s signing secret and shows it once —
          copy it before you leave the page.
        </Note>
      )}

      {showEditor ? (
        <EndpointEditor
          initialUrl={endpoint?.url ?? ''}
          onCancel={() => {
            setEditing(false)
            setDismissedEmptyEditor(true)
          }}
          onSaved={() => {
            setEditing(false)
            setJustSaved(true)
            refresh()
          }}
          storedHeaderName={endpoint?.authHeaderName ?? null}
          variant={variant}
        />
      ) : endpoint ? (
        <ConfiguredRow
          endpoint={endpoint}
          onEdit={
            canEdit
              ? () => {
                  setJustSaved(false)
                  setEditing(true)
                }
              : undefined
          }
        />
      ) : (
        <Note>Not configured yet.</Note>
      )}

      {!canEdit &&
        (ghostTarget !== null ? (
          <Note>Read-only while viewing as a partner.</Note>
        ) : (
          <Note>
            Changing an endpoint needs the <code>{WEBHOOK_WRITE_SCOPE}</code>{' '}
            scope, which your session was issued without. Sign out and back in
            to request it; if it still does not appear, email
            engineering@curri.com.
          </Note>
        ))}
    </Card>
  )
}

export const WebhookEndpointSetup = ({
  attrs,
}: {
  attrs: Record<string, string>
}) => {
  const { state } = useAuth()
  const variant = attrs.variant

  // A directive typo should be loud for the author, not invisible.
  if (!isVariant(variant)) {
    return (
      <Card>
        <Note>
          Unknown webhook-endpoint variant {JSON.stringify(variant ?? null)} —
          use one of {VARIANTS.map(name => `variant="${name}"`).join(', ')}.
        </Note>
      </Card>
    )
  }

  if (state.status !== 'authenticated') return <SignInCard variant={variant} />

  if (!state.caller.grantedScopes.includes(WEBHOOK_READ_SCOPE)) {
    return (
      <Card>
        <Note>
          Your session was issued without the <code>{WEBHOOK_READ_SCOPE}</code>{' '}
          scope, so it cannot read your webhook configuration. Sign out and
          back in to request it.
        </Note>
      </Card>
    )
  }

  return <Configurator variant={variant} />
}
